import React, { useCallback, useEffect, useMemo } from 'react';

import { useSnackbar } from 'notistack';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Link } from '~/lib/react-router-dom';

import { Add, BusinessCenter, ContentCopy, Delete, LocalOffer } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  GridLegacy as Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import DefinitionText from '~/components/Common/Elements/DefinitionText';
import DefinitionTitle from '~/components/Common/Elements/DefinitionTitle';

import {
  useGetRoomContentSources,
  useUpdateRoomContentSources,
} from '~/hooks/apis/accommodation/useRoomContentSources';
import { useUpdateRoom } from '~/hooks/apis/accommodation/useUpdateRoom';

import { PatchRoomPayload, RoomResponse, UpdateRoomContentSourcesPayload } from '~/services/AccommodationService';

import ContentSourceFieldRow, {
  getActiveSource,
  getSelectableSources,
} from '../AccommodationPropertyContentSources/ContentSourceFieldRow';
import useContentSourceFieldStates from '../AccommodationPropertyContentSources/useContentSourceFieldStates';

interface Props {
  roomData: RoomResponse;
  propertyId: string;
  onRoomUpdated?: (room: RoomResponse) => void;
}

interface RoomDetailsFormData {
  name: string;
  numberOfBedrooms: string;
  numberOfBathrooms: string;
  sizeSqm: string;
  views: Array<{ value: string }>;
}

const optionalNumberToString = (value: number | null | undefined) => (value == null ? '' : String(value));

const buildRoomDetailsFormData = (room: RoomResponse): RoomDetailsFormData => ({
  name: room.name || '',
  numberOfBedrooms: optionalNumberToString(room.numberOfBedrooms),
  numberOfBathrooms: optionalNumberToString(room.numberOfBathrooms),
  sizeSqm: optionalNumberToString(room.sizeSqm),
  views: (room.views ?? []).map((view) => ({ value: view })),
});

const parseOptionalNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildPatchRoomPayload = (data: RoomDetailsFormData): PatchRoomPayload => ({
  name: data.name,
  numberOfBedrooms: parseOptionalNumber(data.numberOfBedrooms),
  numberOfBathrooms: parseOptionalNumber(data.numberOfBathrooms),
  sizeSqm: parseOptionalNumber(data.sizeSqm),
  views: data.views.map((view) => view.value.trim()).filter(Boolean),
});

export function AccommodationPropertyRoomDetailsTab({ roomData, propertyId, onRoomUpdated }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const roomViewsKey = JSON.stringify(roomData.views ?? []);
  const roomDetailsFormData = useMemo(
    () => buildRoomDetailsFormData(roomData),
    [roomData.id, roomData.name, roomData.numberOfBedrooms, roomData.numberOfBathrooms, roomData.sizeSqm, roomViewsKey],
  );

  const { control, handleSubmit, watch, reset } = useForm<RoomDetailsFormData>({
    defaultValues: roomDetailsFormData,
  });
  const { fields: viewFields, append: appendView, remove: removeView } = useFieldArray({ control, name: 'views' });

  const formValues = watch();
  const currentPayload = buildPatchRoomPayload(formValues);
  const hasChanges =
    currentPayload.name !== roomData.name ||
    currentPayload.numberOfBedrooms !== roomData.numberOfBedrooms ||
    currentPayload.numberOfBathrooms !== roomData.numberOfBathrooms ||
    currentPayload.sizeSqm !== roomData.sizeSqm ||
    JSON.stringify(currentPayload.views) !== JSON.stringify(roomData.views ?? []);

  useEffect(() => {
    reset(roomDetailsFormData);
  }, [reset, roomDetailsFormData]);

  const { mutate: updateRoom, isPending: isUpdatingRoom } = useUpdateRoom();

  const onSubmit = useCallback(
    (data: RoomDetailsFormData) => {
      updateRoom(
        { propertyId, roomId: roomData.id, data: buildPatchRoomPayload(data) },
        {
          onSuccess: (updatedRoom) => {
            enqueueSnackbar('Room details updated successfully', { variant: 'success' });
            reset(buildRoomDetailsFormData(updatedRoom));
            if (onRoomUpdated) {
              onRoomUpdated(updatedRoom);
            }
          },
          onError: (error: Error) => {
            enqueueSnackbar(`Error updating room details: ${error.message}`, { variant: 'error' });
          },
        },
      );
    },
    [enqueueSnackbar, onRoomUpdated, propertyId, reset, roomData.id, updateRoom],
  );

  return (
    <Stack direction="column" spacing={2}>
      {/* Editable room details */}
      <Card>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack direction="column" spacing={2.5}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
                spacing={2}
              >
                <Typography variant="h6">Room information</Typography>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!hasChanges || isUpdatingRoom}
                  size="small"
                >
                  {isUpdatingRoom ? 'Saving...' : 'Save'}
                </Button>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                }}
              >
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Name"
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={isUpdatingRoom}
                      />
                    )}
                  />
                </Box>

                <Box>
                  <Controller
                    name="numberOfBedrooms"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Number of bedrooms"
                        type="number"
                        size="small"
                        fullWidth
                        disabled={isUpdatingRoom}
                        inputProps={{ min: 0, step: 1 }}
                      />
                    )}
                  />
                </Box>

                <Box>
                  <Controller
                    name="numberOfBathrooms"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Number of bathrooms"
                        type="number"
                        size="small"
                        fullWidth
                        disabled={isUpdatingRoom}
                        inputProps={{ min: 0, step: 1 }}
                      />
                    )}
                  />
                </Box>

                <Box>
                  <Controller
                    name="sizeSqm"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Size (sqm)"
                        type="number"
                        size="small"
                        fullWidth
                        disabled={isUpdatingRoom}
                        inputProps={{ min: 0, step: 1 }}
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
                  <DefinitionTitle>Views</DefinitionTitle>
                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    startIcon={<Add />}
                    disabled={isUpdatingRoom}
                    onClick={() => appendView({ value: '' })}
                  >
                    Add view
                  </Button>
                </Stack>
                <Stack spacing={2}>
                  {viewFields.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No views specified.
                    </Typography>
                  )}
                  {viewFields.map((viewField, index) => (
                    <Stack key={viewField.id} direction="row" spacing={1} alignItems="center">
                      <Controller
                        name={`views.${index}.value`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label={`View ${index + 1}`}
                            size="small"
                            fullWidth
                            disabled={isUpdatingRoom}
                          />
                        )}
                      />
                      <Tooltip title="Delete view">
                        <span>
                          <IconButton
                            aria-label={`Delete view ${index + 1}`}
                            disabled={isUpdatingRoom}
                            onClick={() => removeView(index)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Stack>
  );
}

export function AccommodationPropertyRoomContentTab({ roomData }: Pick<Props, 'roomData'>) {
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: contentSourcesData,
    isLoading: isContentSourcesLoading,
    error: contentSourcesError,
  } = useGetRoomContentSources({ roomId: roomData.id });
  const updateContentSourcesMutation = useUpdateRoomContentSources();
  const {
    getChangedFields,
    getFieldState,
    handleFieldChange,
    hasChanges: hasContentSourceChanges,
  } = useContentSourceFieldStates(contentSourcesData?.fields, contentSourcesData?.availableSources);

  const handleSaveContentSources = useCallback(() => {
    if (!contentSourcesData) {
      return;
    }

    const changedFields = getChangedFields() as UpdateRoomContentSourcesPayload['fields'];
    if (changedFields.length === 0) {
      return;
    }

    updateContentSourcesMutation.mutate(
      { roomId: roomData.id, data: { fields: changedFields } },
      {
        onSuccess: () => {
          enqueueSnackbar('Room content sources updated', { variant: 'success' });
        },
        onError: (err: Error) => {
          enqueueSnackbar(`Failed to update: ${err.message}`, { variant: 'error' });
        },
      },
    );
  }, [contentSourcesData, getChangedFields, roomData.id, updateContentSourcesMutation, enqueueSnackbar]);

  // Room content source editing is currently scoped to the main description field.
  const descriptionFieldSelection = contentSourcesData?.fields.find((f) => f.field === 'description');

  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Box>
            <Typography variant="h6">Content</Typography>
            <Typography variant="body2" color="text.secondary">
              Select the source override.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fields left empty have the best source selected when serving the content.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSaveContentSources}
            disabled={updateContentSourcesMutation.isPending || !hasContentSourceChanges}
          >
            Save
          </Button>
        </Stack>

        {contentSourcesError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            Error loading content sources:{' '}
            {contentSourcesError instanceof Error ? contentSourcesError.message : 'Unknown error'}
          </Alert>
        )}

        {isContentSourcesLoading && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!isContentSourcesLoading && !descriptionFieldSelection && (
          <Typography variant="body2" color="text.secondary">
            No editable room content sources are available for this room.
          </Typography>
        )}

        {descriptionFieldSelection && contentSourcesData && (
          <ContentSourceFieldRow
            fieldSelection={descriptionFieldSelection}
            activeSource={getActiveSource(descriptionFieldSelection, contentSourcesData.availableSources)}
            availableSources={getSelectableSources(descriptionFieldSelection, contentSourcesData.availableSources)}
            fieldState={getFieldState(descriptionFieldSelection)}
            onChange={handleFieldChange}
            isPending={updateContentSourcesMutation.isPending}
          />
        )}
      </CardContent>
    </Card>
  );
}

export function AccommodationPropertyRoomPackagesTab({ roomData }: Pick<Props, 'roomData'>) {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const handleCopyClick = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar(`${label} copied to clipboard`, { variant: 'success' });
  };

  const getStatusChipColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'content-approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatStatus = (status: string) => {
    return status
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Stack direction="column" spacing={2}>
      {/* Offer Packages */}
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <BusinessCenter />
            <Typography variant="h6">Offer Packages ({roomData.offerPackages.length})</Typography>
          </Stack>

          {roomData.offerPackages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No offer packages available for this room.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {roomData.offerPackages.map((offerPackage, index) => (
                <Accordion key={offerPackage.id} defaultExpanded={index === 0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                      <LocalOffer color="primary" />
                      <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                        {offerPackage.name}
                      </Typography>
                      <Chip
                        label={formatStatus(offerPackage.status)}
                        color={getStatusChipColor(offerPackage.status)}
                        size="small"
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ backgroundColor: theme.palette.grey[50] }}>
                    <Stack spacing={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <DefinitionTitle>LE Package ID</DefinitionTitle>
                          <DefinitionText>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {offerPackage.lePackageId}
                              </Typography>
                              <Tooltip title="Copy LE Package ID">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyClick(offerPackage.lePackageId, 'LE Package ID')}
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </DefinitionText>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <DefinitionTitle>Salesforce External ID</DefinitionTitle>
                          <DefinitionText>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {offerPackage.idSalesforceExternal}
                              </Typography>
                              <Tooltip title="Copy Salesforce External ID">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCopyClick(offerPackage.idSalesforceExternal, 'Salesforce External ID')
                                  }
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </DefinitionText>
                        </Grid>
                      </Grid>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <DefinitionTitle>Reservation Room ID</DefinitionTitle>
                          <DefinitionText>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {offerPackage.reservationRoomId}
                              </Typography>
                              <Tooltip title="Copy Reservation Room ID">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyClick(offerPackage.reservationRoomId, 'Reservation Room ID')}
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </DefinitionText>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <DefinitionTitle>Reservation Property ID</DefinitionTitle>
                          <DefinitionText>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {offerPackage.reservationPropertyId}
                              </Typography>
                              <Tooltip title="Copy Reservation Property ID">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCopyClick(offerPackage.reservationPropertyId, 'Reservation Property ID')
                                  }
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </DefinitionText>
                        </Grid>
                      </Grid>

                      <Divider />

                      <Box>
                        <DefinitionTitle>Package Options</DefinitionTitle>
                        <DefinitionText>
                          {offerPackage.packageOptions.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No package options available
                            </Typography>
                          ) : (
                            <Box>
                              {offerPackage.packageOptions.map((option) => (
                                <Stack
                                  direction="row"
                                  gap={2}
                                  spacing={2}
                                  key={option.roomRateId}
                                  justifyContent="space-between"
                                  alignItems="center"
                                  mb={1}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {option.name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontFamily: 'monospace' }}
                                    >
                                      Rate ID: {option.roomRateId}
                                    </Typography>
                                    <Tooltip title="Copy Rate ID">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleCopyClick(option.roomRateId, 'Rate ID')}
                                      >
                                        <ContentCopy fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                  <Link
                                    to={`/vendors/unknown/properties/${offerPackage.reservationPropertyId}/room-types/${offerPackage.reservationRoomId}/room-rates/${option.roomRateId}`}
                                    target="_blank"
                                  >
                                    <Button variant="contained" color="primary">
                                      View Rate
                                    </Button>
                                  </Link>
                                </Stack>
                              ))}
                            </Box>
                          )}
                        </DefinitionText>
                      </Box>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}

export default function AccommodationPropertyRoomViewDetail({ roomData, propertyId, onRoomUpdated }: Props) {
  return (
    <Stack direction="column" spacing={2}>
      <AccommodationPropertyRoomDetailsTab roomData={roomData} propertyId={propertyId} onRoomUpdated={onRoomUpdated} />
      <AccommodationPropertyRoomContentTab roomData={roomData} />
      <AccommodationPropertyRoomPackagesTab roomData={roomData} />
    </Stack>
  );
}
